import { MentionFrequencyMapDoc } from "libs/schema";
import { MentionableUser, observeMentionableUsers } from "~/observables/observeMentionableUsers";
import { MentionableGroup, observeMentionableGroups } from "~/observables/observeMentionableGroups";
import { combineLatest, map, Observable, of } from "rxjs";
import { ObserveOptions } from "~/environment/RecordLoader";
import { observeCurrentUserMentionFrequency } from "~/observables/observeCurrentUserMentionFrequency";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { cacheReplayForTime } from "libs/rxjs-operators";

// Note, this function is in it's own module because it is imported by `createEnvironmentBase.ts`.
// Previously it was defined in the MentionDropdown module, but this caused a circular dependency
// issue.

export type ObserveMentionDropdownDataResult = {
  users: MentionableUser[];
  groups: MentionableGroup[];
  mentions: MentionFrequencyMapDoc | null;
  deactivatedUsers: MentionableUser[];
  isLoading: boolean;
};

export function observeMentionDropdownData(
  environment: Pick<ClientEnvironment, "recordLoader" | "subscriptionManager" | "logger">,
  props: { currentUserId: string; threadId: string | null | undefined; includeDeactivatedUsers?: boolean },
  options?: ObserveOptions,
): Observable<ObserveMentionDropdownDataResult> {
  const cacheKey =
    props.currentUserId + props.includeDeactivatedUsers + props.threadId + options?.fetchStrategy + options?.isLoading;

  const cachedQuery = queryCache.get(cacheKey);

  if (cachedQuery) return cachedQuery;

  const observable = combineLatest([
    observeMentionableUsers(environment, { currentUserId: props.currentUserId }, options),
    observeMentionableGroups(environment, { currentUserId: props.currentUserId }, options),
    observeCurrentUserMentionFrequency(environment, { currentUserId: props.currentUserId }, options),
    props.includeDeactivatedUsers ?
      observeMentionableUsers(environment, { currentUserId: props.currentUserId, onlyDeactivatedUsers: true }, options)
    : of(observeMentionableUsers.EMPTY_RESULT()),
    props.threadId ?
      environment.recordLoader.observeGetThreadRequests({ thread_id: props.threadId }, options)
    : environment.recordLoader.createObserveQueryResult<"thread_request">(),
  ]).pipe(
    map(([users, groups, mentions, deactivatedUsers, requests]) => ({
      users: users[0],
      groups: groups[0],
      mentions: mentions[0],
      deactivatedUsers: deactivatedUsers[0],
      requests: requests[0],
      isLoading:
        users[1].isLoading ||
        groups[1].isLoading ||
        mentions[1].isLoading ||
        deactivatedUsers[1].isLoading ||
        requests[1].isLoading,
    })),
    cacheReplayForTime({
      timeMs: 100,
      onInit() {
        queryCache.set(cacheKey, observable);
      },
      onCleanup() {
        queryCache.delete(cacheKey);
      },
    }),
  );

  return observable;
}

const queryCache = new Map<string, Observable<ObserveMentionDropdownDataResult>>();

/* -----------------------------------------------------------------------------------------------*/
